// src/pages/Admin/VotePage/components/VotesDesktopTable.jsx

import React from "react";

import { FaChartPie, FaEdit, FaListUl, FaTrash } from "react-icons/fa";

import AudienceChips from "../ui/AudienceChips";
import VoteActionButton from "../ui/VoteActionButton";
import VoteStatusPill from "../ui/VoteStatusPill";
import VoteTypePill from "../ui/VoteTypePill";
import VoteClubRulesPills from "../ui/VoteClubRulesPills";

import { formatVotePeriod } from "../utils/voteAdmin.utils";

function DesktopSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {[1, 2, 3, 4].map((item) => (
        <div
          key={item}
          className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}

export default function VotesDesktopTable({
  votes,
  loading,
  deletingId,
  actorGroups,
  onEdit,
  onQuestions,
  onResults,
  onDelete,
}) {
  return (
    <section className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 lg:block">
      {loading ? (
        <DesktopSkeleton />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50/90 text-left dark:bg-slate-800/80">
              <tr>
                <th className="px-4 py-3 font-black text-slate-500 dark:text-slate-300">
                  Consultation
                </th>

                <th className="px-4 py-3 font-black text-slate-500 dark:text-slate-300">
                  Type
                </th>

                <th className="px-4 py-3 font-black text-slate-500 dark:text-slate-300">
                  Statut
                </th>

                <th className="px-4 py-3 font-black text-slate-500 dark:text-slate-300">
                  Période
                </th>

                <th className="px-4 py-3 font-black text-slate-500 dark:text-slate-300">
                  Audience
                </th>

                <th className="px-4 py-3 text-center font-black text-slate-500 dark:text-slate-300">
                  Questions
                </th>

                <th className="px-4 py-3 text-center font-black text-slate-500 dark:text-slate-300">
                  Votants
                </th>

                <th className="px-4 py-3 text-right font-black text-slate-500 dark:text-slate-300">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {votes.map((vote) => (
                <tr
                  key={vote.id}
                  className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/50"
                >
                  <td className="max-w-[320px] px-4 py-4 align-top">
                    <div className="font-black leading-snug text-slate-900 dark:text-white">
                      {vote.title}
                    </div>

                    {vote.description && (
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                        {vote.description}
                      </p>
                    )}

                    <VoteClubRulesPills vote={vote} className="mt-2" />
                  </td>

                  <td className="whitespace-nowrap px-4 py-4 align-top">
                    <VoteTypePill
                      type={vote.type}
                      isAnonymous={vote.is_anonymous}
                    />
                  </td>

                  <td className="whitespace-nowrap px-4 py-4 align-top">
                    <VoteStatusPill status={vote._status} />
                  </td>

                  <td className="whitespace-nowrap px-4 py-4 align-top">
                    <div className="font-bold text-slate-700 dark:text-slate-200">
                      {formatVotePeriod(vote)}
                    </div>

                    <div className="mt-1 text-xs text-slate-400">
                      {vote.is_active
                        ? "Publication autorisée"
                        : "Publication désactivée"}
                    </div>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <AudienceChips
                      roleIds={vote._allowedRoleIds}
                      actorGroupIds={vote._allowedActorGroupIds}
                      actorGroups={actorGroups}
                      selectedActorGroups={vote.allowed_actor_groups ?? []}
                    />
                  </td>

                  <td className="px-4 py-4 text-center align-top">
                    <span className="inline-flex min-w-9 items-center justify-center rounded-xl bg-indigo-50 px-2 py-1 font-black text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                      {vote.questionCount ?? 0}
                    </span>
                  </td>

                  <td className="px-4 py-4 text-center align-top">
                    <span className="inline-flex min-w-9 items-center justify-center rounded-xl bg-emerald-50 px-2 py-1 font-black text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                      {vote.voterCount ?? 0}
                    </span>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <div className="flex justify-end gap-2">
                      <VoteActionButton
                        icon={FaEdit}
                        label="Modifier"
                        compact
                        onClick={() => onEdit(vote)}
                      />

                      <VoteActionButton
                        icon={FaListUl}
                        label="Questions"
                        compact
                        variant="indigo"
                        onClick={() => onQuestions(vote)}
                      />

                      <VoteActionButton
                        icon={FaChartPie}
                        label="Résultats"
                        compact
                        variant="violet"
                        onClick={() => onResults(vote)}
                      />

                      <VoteActionButton
                        icon={FaTrash}
                        label="Supprimer"
                        compact
                        variant="danger"
                        disabled={deletingId === vote.id}
                        onClick={() => onDelete(vote)}
                      />
                    </div>
                  </td>
                </tr>
              ))}

              {!votes.length && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-sm font-bold text-slate-500"
                  >
                    Aucun vote ne correspond à votre recherche.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
